<template>
  <div>
    <v-select
      v-model="selectedCompanyId"
      label="Firma auswählen"
      :items="companies"
      :loading="companiesLoading"
      :disabled="tableLoading"
      item-text="name"
      item-value="id"
      clearable
      outlined
      solo
    >
      <template slot="append-outer">
        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              :disabled="submitting || tableLoading"
              icon
              v-bind="attrs"
              v-on="on"
              @click="() => { dialogAddCompany = true; addCompanyName = ''; }"
            >
              <v-icon>
                mdi-plus
              </v-icon>
            </v-btn>
          </template>
          <span>Firma hinzufügen</span>
        </v-tooltip>
      </template>
    </v-select>
    <v-data-table
      class="elevation-1"
      item-key="id"
      loading-text="Projekte werden geladen"
      :headers="tableHeaders"
      :items="projectsForSelectedCompany"
      :search="tableSearch"
      :loading="tableLoading"
      :options="{
        sortBy: ['projektId'],
        sortDesc: [false]
      }"
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [5, 10, 25, 50]
      }"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>
            {{ selectedCompany ? 'Projekte für ' + selectedCompany.name : 'Keine Firma ausgewählt' }}
          </v-toolbar-title>
          <v-divider
            class="mx-4"
            inset
            vertical
          ></v-divider>
          <v-text-field
            v-model="tableSearch"
            prepend-icon="mdi-magnify"
            class="mr-4"
            clearable
            hide-details
            single-line
            dense
          ></v-text-field>
          <v-divider
            class="mx-4"
            inset
            vertical
          ></v-divider>
          <v-dialog
            v-model="dialogAssignProjectToCompany"
            max-width="500px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                :disabled="submitting || tableLoading"
                text
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left>
                  {{ icons.mdiTablePlus }}
                </v-icon>
                Projekt zuteilen
              </v-btn>
            </template>
            <v-card v-if="selectedCompany !== null">
              <v-card-title>
                {{ selectedCompany.name }}
                <v-divider
                  class="mx-4"
                  vertical
                ></v-divider>
                Projekt zuteilen
              </v-card-title>
              <v-card-text>
                <search-project-input
                  :projectFilter="projectFilter"
                  @projectSelected="selectedAssignProject = $event"
                />
                <div v-if="isAssignProjectSelected">
                  <span>Bitte Projekt auswählen</span>
                </div>
                <template v-else-if="isSelectedProjectAlreadyAssigned">
                  <v-alert
                    type="warning"
                    outlined
                    dense
                  >
                    Das ausgewählte Projekt ist bereits der Firma <b>{{ assignedCompanyToSelectedProject.name }}</b> zugeteilt!
                  </v-alert>
                </template>
                <span
                  v-else
                  class="text-h6"
                >
                  Projekt <b>{{ selectedAssignProject.projektId }} - {{ selectedAssignProject.name }}</b>
                  der Firma <b>{{ selectedCompany.name }}</b> zuteilen?
                </span>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="warning"
                  :loading="submitting"
                  :disabled="isSelectedProjectAlreadyAssigned || selectedAssignProject === null || submitting"
                  depressed
                  text
                  @click="assignCompany"
                >
                  Projekt zuteilen
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="submitting"
                  depressed
                  text
                  @click="selectedAssignProject = null; dialogAssignProjectToCompany = false;"
                >
                  Abbrechen
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:[`item.projectStatus`]="{ item }">
        <span :style="{ color: item.projectStatus.color }">
          {{ item.projectStatus.description }}
        </span>
      </template>
      <template v-slot:[`item.distributionStatus`]="{ item }">
        <span :style="{ color: item.distributionStatus.color }">
          {{ item.distributionStatus.description }}
        </span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              :disabled="submitting || tableLoading"
              small
              color="red"
              class="mr-2"
              v-bind="attrs"
              v-on="on"
              @click="unassignProjectDialog(item)"
            >
              {{ icons.mdiCancel }}
            </v-icon>
          </template>
          <span>Projekt {{ item.name }} entziehen</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <v-dialog
      v-model="dialogAddCompany"
      width="max(20vw, 350px)"
    >
      <v-card :loading="submitting">
        <v-card-title>Neue Firma anlegen</v-card-title>
        <v-card-text>
          <v-form
            ref="addCompanyForm"
            v-model="addCompanyFormValid"
            :disabled="submitting"
          >
            <v-container>
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="addCompanyName"
                    label="Firmenname"
                    :rules="companyRules"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="warning"
            :loading="submitting"
            :disabled="!addCompanyFormValid || submitting"
            depressed
            text
            @click="addCompany"
          >
            Neue Firma anlegen
          </v-btn>
          <v-btn
            color="primary"
            :disabled="submitting"
            depressed
            text
            @click="dialogAddCompany = !dialogAddCompany"
          >
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="unassignDialog"
      width="max(20vw, 350px)"
    >
      <v-card
        v-if="unassignProject !== null"
        :loading="submitting"
      >
        <v-card-title>Projekt {{ unassignProject.projektId }} - {{ unassignProject.name }}</v-card-title>
        <v-card-text>
          Soll das Projekt <b>{{ unassignProject.projektId }} - {{ unassignProject.name }}</b>
          wirklich der Firma <b>{{ selectedCompany.name }}</b> entzogen werden?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="warning"
            :loading="submitting"
            :disabled="submitting"
            depressed
            text
            @click="unassignCompany"
          >
            Ja, Projekt entziehen
          </v-btn>
          <v-btn
            color="primary"
            :disabled="submitting"
            depressed
            text
            @click="unassignDialog = false; unassignProject = null;"
          >
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { HTTP } from '@/main/httpClient.js';
import { showNotification } from '@/main/NotificationObject.js';
import { mdiTablePlus, mdiCancel } from '@mdi/js';
import SearchProjectInput from '../../components/SearchProjectInput.vue';

export default {
  name: 'ConstructionCompanyManage',
  components: {
    SearchProjectInput
  },
  props: {
    companies: {
      type: Array,
      required: true
    },
    companiesLoading: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    icons: { mdiTablePlus, mdiCancel },
    selectedCompanyId: null,
    projectsForSelectedCompany: [],
    dialogAddCompany: false,
    dialogAssignProjectToCompany: false,
    selectedAssignProject: null,
    addCompanyName: '',
    addCompanyFormValid: false,
    submitting: false,
    tableSearch: null,
    tableLoading: false,
    unassignProject: null,
    unassignDialog: false,
    companyRules: [
      (v) => !!v || 'Firmenname ist ein Pflichtfeld',
      (v) =>
        v.length >= 5 || 'Der Firmenname muss mindestens 5 Zeichen lang sein'
    ]
  }),
  computed: {
    selectedCompany() {
      if (!this.selectedCompanyId) return null;
      return this.companies.find((c) => c.id === this.selectedCompanyId);
    },
    tableHeaders() {
      return [
        { text: 'Project ID', value: 'projektId', width: '120px' },
        { text: 'Name', value: 'name' },
        { text: 'Ort', value: 'city' },
        { text: 'Ortsteil', value: 'district' },
        { text: 'Landkreis', value: 'state' },
        { text: 'Projektstatus', value: 'projectStatus' },
        { text: 'Vertriebsphase', value: 'distributionStatus' },
        { text: 'Aktionen', align: 'end', sortable: false, value: 'actions' }
      ];
    },
    isAssignProjectSelected() {
      return (
        this.selectedAssignProject === null ||
        typeof this.selectedAssignProject !== 'object'
      );
    },
    isSelectedProjectAlreadyAssigned() {
      if (this.selectedAssignProject === null) return false;
      return this.selectedAssignProject.subcontractorHouseConnectionId !== null;
    },
    assignedCompanyToSelectedProject() {
      if (!this.selectedAssignProject) return;
      if (!this.subcontractorHouseConnectionId) return;
      return this.companies.find(
        (c) =>
          c.id === this.selectedAssignProject.subcontractorHouseConnectionId
      );
    }
  },
  watch: {
    companies(newVal, oldVal) {
      if (oldVal.length === 0 && newVal.length > 0) {
        const { search } = document.location;
        const params = new URLSearchParams(search.substring(1));
        if (params.has('company')) {
          this.selectedCompanyId = params.get('company');
        } else {
          this.selectedCompanyId = newVal[0].id;
        }
      }
    },
    selectedCompanyId(newVal) {
      this.fetchProjects();

      if (this.$route.query.company === newVal) return;
      const { hash } = document.location;
      this.$router.push({
        name: this.$route.name,
        query: { company: newVal },
        hash
      });
    }
  },
  created() {
    if (this.companies.length > 0) {
      this.selectedCompanyId = this.companies[0].id;
    }
  },
  methods: {
    addCompany() {
      if (!this.addCompanyFormValid) return;
      this.submitting = true;
      HTTP.post('/past/companies', {
        name: this.addCompanyName
      })
        .then((res) => {
          showNotification(
            `Die Firma ${this.addCompanyName} wurde erfolgreich angelegt`,
            'success'
          );
          this.addCompanyName = '';
          this.dialogAddCompany = false;
          this.$emit('update-companies');
        })
        .catch(() => {
          showNotification('Fehler beim anlegen einer neuen Firma', 'error');
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    fetchProjects() {
      if (!this.selectedCompanyId) return;
      this.tableLoading = true;

      const query = `?where[subcontractorHouseConnectionId][equals]=${this.selectedCompanyId}`;

      HTTP.get('past/projects' + query)
        .then(({ status, data }) => {
          if (status === 200) {
            this.projectsForSelectedCompany = data;
          }
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    unassignProjectDialog(project) {
      this.unassignProject = project;
      this.unassignDialog = true;
    },
    unassignCompany() {
      this.submitting = true;
      const body = {
        subcontractorHouseConnectionId: null
      };
      HTTP.patch(
        `/productAvailabilityTool/project/${this.unassignProject.projektId}/unassignSubcontractorHC`,
        body
      )
        .then(() => {
          this.fetchProjects();
          this.unassignDialog = false;
          this.unassignProject = null;
          showNotification('Projekt wurde erfolgreich entzogen', 'success');
        })
        .catch((err) => {
          console.error(err);
          showNotification('Fehler beim entziehen der Projekt', 'error');
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    assignCompany() {
      this.submitting = true;
      const body = {
        subcontractorHouseConnectionId: this.selectedCompany.id
      };
      HTTP.patch(
        `/productAvailabilityTool/project/${this.selectedAssignProject.projektId}`,
        body
      )
        .then(() => {
          this.fetchProjects();
          this.selectedAssignProject = null;
          this.dialogAssignProjectToCompany = false;
          showNotification(
            'Das Projekt wurde erfolgreich der Firma zugeteilt',
            'success'
          );
        })
        .catch((err) => {
          console.error(err);
          showNotification(
            'Beim zuteilen des Projektes ist ein Fehler aufgetreten',
            'error'
          );
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    projectSelected(project) {
      console.log('project selected', project);
    },
    projectFilter(projects) {
      return projects.filter(
        (p) =>
          p.subcontractorHouseConnectionId === null ||
          p.subcontractorHouseConnectionId !== this.selectedCompany.id
      );
    }
  }
};
</script>
